import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, CheckCircle, Plus, MoreVertical, Calendar, 
  Truck, User, Tag, CreditCard, LayoutGrid, X, AlertTriangle,
  RotateCcw, RefreshCw, Phone, Download, Eye, History, ChevronDown,
  Clock, MapPin, Package, ClipboardList, Info
} from 'lucide-react';
import { Dropdown, Checkbox, message, Tooltip, Modal, Drawer, Divider } from 'antd';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import ExcelExportButton from '../components/ExcelExportButton';
import { useTheme } from '../context/ThemeContext';
import { formatDate, formatCurrency } from '../utils/helpers';
import whatsappIcon from "../assets/pngegg2.png"; 

function DispatchFailed() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  
  // Filter States
  const [filters, setFilters] = useState({
    dateRange: 'Select Date',
    category: 'All Categories',
    paymentMode: 'All Modes',
    campaign: 'All Campaigns',
    orderBy: 'All Users',
    actionBy: 'All Users'
  });

  const [allOrders] = useState([
    {
      id: 1,
      orderId: "OD3455",
      orderDate: "23/03/2024, 05:09 PM",
      customerName: "Hemant Mehto",
      contactNumber: "XXXXXX2987",
      productName: "3A Piles Care Capsules",
      category: "Ayurvedic Men Wellness",
      restAmount: 1500,
      paymentMode: "COD",
      campaign: "F-Lead",
      orderedBy: "Manisha Singh",
      orderedByEmp: "1239",
      actionBy: "Nitesh Singh",
      actionByEmp: "1239",
      remarks: "Ringing",
      status: "Pincode Not Serviceable",
      address: "123, Green Park, South Delhi, Delhi - 110016",
      pincode: "110016",
      city: "Delhi"
    },
    {
      id: 2,
      orderId: "OD3456",
      orderDate: "23/03/2024, 04:33 PM",
      customerName: "Sumit Kumar Upadhyay",
      contactNumber: "XXXXXX2987",
      productName: "Shilajit 25-Gold 30 Capsules",
      category: "Ayurvedic Men Wellness",
      restAmount: 5480,
      paymentMode: "COD",
      campaign: "F-Lead",
      orderedBy: "Dilip Singh Bisht",
      orderedByEmp: "1123",
      actionBy: "Arihant Sagar",
      actionByEmp: "1123",
      remarks: "Customer want call back",
      status: "Pincode Not Serviceable",
      address: "Plot No. 45, Sector 12, Noida, UP - 201301",
      pincode: "201301",
      city: "Noida"
    },
    {
      id: 3,
      orderId: "OD3457",
      orderDate: "23/03/2024, 04:41 PM",
      customerName: "Dilip Topi",
      contactNumber: "XXXXXX2987",
      productName: "Aaroprotein Kid",
      category: "Ayurvedic",
      restAmount: 400,
      paymentMode: "COD",
      campaign: "F-Lead",
      orderedBy: "Nalini Singh",
      orderedByEmp: "1234",
      actionBy: "Sumit Kuma",
      actionByEmp: "1234",
      remarks: "Ringing",
      status: "Pincode Not Serviceable",
      address: "Near Hanuman Mandir, Shimla, HP - 171001",
      pincode: "171001",
      city: "Shimla"
    }
  ]);

  // Filtering Logic
  const filteredOrders = useMemo(() => {
    if (!searchTerm) return allOrders;
    return allOrders.filter(o => 
      o.orderId.toLowerCase().includes(searchTerm.toLowerCase()) || 
      o.customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allOrders, searchTerm]);

  // Pagination Logic
  const totalEntries = filteredOrders.length;
  const totalPages = Math.ceil(totalEntries / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);
  const showingCount = paginatedOrders.length;

  const tableHeaderStyle = { background: currentTheme.color || '#459B7A' };

  const handleExport = () => {
    setIsExporting(true);
    message.loading('Preparing Excel export...');
    setTimeout(() => {
      setIsExporting(false);
      message.success('Excel file downloaded successfully');
    }, 2000);
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: 'Select Date',
      category: 'All Categories',
      paymentMode: 'All Modes',
      campaign: 'All Campaigns',
      orderBy: 'All Users',
      actionBy: 'All Users'
    });
    message.success('Filters reset successfully');
  };

  const handleApplyFilters = () => {
    message.loading('Applying filters...', 0.5);
    setTimeout(() => {
        setIsFilterModalOpen(false);
        message.success('Filters applied successfully');
    }, 500);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleOrderDetails = (order) => {
    navigate(`/order-details/${order.orderId}`);
  };

  // Static Dropdown Menus
  const menuItems = {
    dateRange: [
        { key: 'Today', label: 'Today' },
        { key: 'Yesterday', label: 'Yesterday' },
        { key: 'Last 7 Days', label: 'Last 7 Days' },
        { key: 'Last 30 Days', label: 'Last 30 Days' },
    ],
    category: [
        { key: 'All Categories', label: 'All Categories' },
        { key: 'Ayurvedic Men Wellness', label: 'Ayurvedic Men Wellness' },
        { key: 'Ayurvedic', label: 'Ayurvedic' },
    ],
    paymentMode: [
        { key: 'All Modes', label: 'All Modes' },
        { key: 'COD', label: 'COD' },
        { key: 'Prepaid', label: 'Prepaid' },
    ],
    campaign: [
        { key: 'All Campaigns', label: 'All Campaigns' },
        { key: 'F-Lead', label: 'F-Lead' },
        { key: 'OJT', label: 'OJT' },
    ],
    users: [
        { key: 'All Users', label: 'All Users' },
        { key: 'Manisha Singh', label: 'Manisha Singh' },
        { key: 'Dilip Singh Bisht', label: 'Dilip Singh Bisht' },
    ]
  };

  const renderDropdown = (key, items, icon) => (
    <Dropdown 
        menu={{ 
          items, 
          onClick: ({ key: val }) => updateFilter(key, val) 
        }}
        trigger={['click']}
    >
        <div className="relative group cursor-pointer flex items-center justify-between rounded-lg bg-[#E2E8F0] px-4 py-2.5 text-[#4A5568] hover:bg-[#CBD5E0] transition-colors border-2 border-transparent">
            <div className="flex items-center gap-3">
                {icon}
                <span className="font-bold text-sm">{filters[key]}</span>
            </div>
            <ChevronDown size={18} className="text-[#718096]" />
        </div>
    </Dropdown>
  );

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50/30'}`}>
      <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-black text-[#1a3b32]">
          Dispatch Failed <span className="text-gray-400 font-medium">({totalEntries})</span>
        </h1>
        <div className="flex items-center gap-2">
            <button 
                onClick={() => setIsFilterModalOpen(true)}
                className="p-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all">
              <Filter className="w-6 h-6" style={{ color: currentTheme.color }}/>
            </button>
            <ExcelExportButton 
                onClick={handleExport}
                isDownloading={isExporting}
                label="EXPORT"
                className="scale-90"
            />
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center gap-4">
        <ExpandableSearchBar 
          searchTerm={searchTerm}
          onSearchChange={(v) => { setSearchTerm(v); setCurrentPage(1); }}
          placeholder="Search by order id or customer name"
        />
        
        <select 
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-medium text-gray-700 outline-none focus:ring-2 focus:ring-opacity-50 min-w-[120px]"
          style={{ borderColor: currentTheme.color }}>
          <option value={20}>20 Orders</option>
          <option value={50}>50 Orders</option>
          <option value={100}>100 Orders</option>
        </select>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-white" style={tableHeaderStyle}>
              <tr>
                <th className="px-4 py-3 text-left">
                  <div className="flex items-center gap-1 justify-center bg-white/20 rounded p-1 w-8 h-8">
                    <Plus size={16} />
                    <span className="text-[10px] font-black">ALL</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">ORDER ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">DATE</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">NAME</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">PRODUCT</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">CATEGORY</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">REST AMT.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">PAY MODE</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">ORDER CAMPAIGN</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">ORDER BY</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">ACTION BY</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">REMARKS</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">STATUS</th>
                <th className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap uppercase">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedOrders.map((order, index) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleOrderDetails(order)}>
                  <td className="px-4 py-4 text-center">
                    <div className="inline-flex p-1 border border-green-500 rounded-full text-green-500">
                      <Plus size={14} />
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{order.orderId}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{order.orderDate}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-gray-900">{order.customerName}</span>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Phone size={10} className="text-green-500" />
                        <span>{order.contactNumber}</span>
                        <img src={whatsappIcon} alt="WA" className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{order.productName}</td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-gray-600 leading-tight block max-w-[120px]">
                      {order.category}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-bold text-green-600">₹ {order.restAmount}</span>
                  </td>
                  <td className="px-4 py-4 text-sm font-bold text-gray-700">{order.paymentMode}</td>
                  <td className="px-4 py-4 text-sm text-gray-700 font-bold">{order.campaign}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">{order.orderedBy}</span>
                      <span className="text-xs text-gray-400 font-bold">Emp: {order.orderedByEmp}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">{order.actionBy}</span>
                      <span className="text-xs text-gray-400 font-bold">Emp: {order.actionByEmp}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700 font-medium">
                    {order.remarks}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 bg-orange-50 text-orange-400 border border-orange-200 rounded-md text-[11px] font-bold">
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                       <button 
                            onClick={() => handleOrderDetails(order)}
                            className="p-1.5 hover:bg-gray-100 rounded text-[#459B7A]">
                         <Eye size={18} />
                       </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

       {/* Footer info */}
       <div className="flex items-center justify-between mt-4 text-sm text-gray-500 font-medium">
          <span>Showing {showingCount}/{totalEntries} entries</span>
          <div className="flex items-center gap-2">
            <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-2 border border-gray-200 rounded-lg ${currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                {"<"}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button 
                key={n} 
                onClick={() => setCurrentPage(n)}
                className={`w-8 h-8 rounded-lg border border-gray-200 transition-all ${currentPage === n ? 'bg-green-500 text-white border-green-500' : 'hover:bg-gray-50'}`}>
                {n}
              </button>
            ))}
            <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`p-2 border border-gray-200 rounded-lg ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                {">"}
            </button>
          </div>
       </div>

       {/* Filter Modal */}
       <Modal
          title={null}
          open={isFilterModalOpen}
          onCancel={() => setIsFilterModalOpen(false)}
          footer={null}
          width={800}
          centered
          styles={{ 
            content: { padding: 0, borderRadius: '12px', overflow: 'hidden' },
            body: { padding: 0 }
          }}
          closable={false}
       >
             {/* Header */}
             <div className="flex items-center justify-between bg-[#718096] px-6 py-4 text-white">
                <span className="text-xl font-bold tracking-tight">Filter Orders</span>
                <button onClick={() => setIsFilterModalOpen(false)} className="hover:opacity-70 transition-opacity">
                   <X size={24} />
                </button>
             </div>

             {/* Content */}
             <div className="grid grid-cols-2 gap-x-8 gap-y-6 px-8 py-8 bg-white">
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Date Range</label>
                   {renderDropdown('dateRange', menuItems.dateRange, <Calendar size={18} className="text-[#718096]" />)}
                </div>
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Category</label>
                   {renderDropdown('category', menuItems.category, <Tag size={18} className="text-[#718096]" />)}
                </div>
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Payment Mode</label>
                   {renderDropdown('paymentMode', menuItems.paymentMode, <CreditCard size={18} className="text-[#718096]" />)}
                </div>
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Campaign</label>
                   {renderDropdown('campaign', menuItems.campaign, <LayoutGrid size={18} className="text-[#718096]" />)}
                </div>
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Order By</label>
                   {renderDropdown('orderBy', menuItems.users, <User size={18} className="text-[#718096]" />)}
                </div>
                <div className="space-y-2">
                   <label className="text-sm font-bold text-[#4A5568]">Action By</label>
                   {renderDropdown('actionBy', menuItems.users, <User size={18} className="text-[#718096]" />)}
                </div>
             </div>

             <div className="flex items-center justify-end gap-4 border-t border-gray-100 px-8 py-6 bg-white">
                <button onClick={handleResetFilters} className="rounded-lg border border-gray-300 px-8 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-50 transition-all hover:border-gray-400">Reset</button>
                <button onClick={handleApplyFilters} className="rounded-lg bg-[#718096] px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-[#4A5568] transition-all hover:scale-[1.02] active:scale-[0.98]">Apply Filters</button>
             </div>
       </Modal>
      </div>
    </div>
  );
}

export default DispatchFailed;
